/**
 * Cliente HTTP del portal.
 *
 * El backend responde `{ error: { code } }` donde `code` es una clave de i18n:
 * el error se propaga con esa clave y cada pantalla decide cómo mostrarlo.
 */

const BASE_URL = import.meta.env?.VITE_API_URL ?? "/api";

// Una recarga breve del backend o del proxy no debe expulsar al usuario de la
// pantalla en la que estaba trabajando. Las lecturas se recuperan con una
// espera progresiva; las escrituras nunca se repiten porque podrían duplicar
// una acción real.
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const READ_RETRY_DELAYS_MS = [300, 700, 1400, 2800];

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export class ApiError extends Error {
  constructor(status, code, details) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details ?? null;
  }
}

async function request(
  path,
  {
    method = "GET",
    body,
    signal,
    isFormData,
    retry = method === "GET",
  } = {},
) {
  let response;

  // Solo las lecturas son seguras para un reintento automático. Repetir un
  // POST tras un corte podría crear dos solicitudes o enviar dos avisos.
  const attempts = retry && method === "GET" ? READ_RETRY_DELAYS_MS.length + 1 : 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        // Las cookies de sesión son imprescindibles en cada llamada.
        credentials: "include",
        headers: isFormData ? undefined : { "Content-Type": "application/json" },
        body: isFormData ? body : body ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      if (error.name === "AbortError") throw error;
      if (attempt + 1 < attempts) {
        await wait(READ_RETRY_DELAYS_MS[attempt]);
        continue;
      }
      throw new ApiError(0, "errors.network");
    }

    if (
      attempt + 1 < attempts &&
      RETRYABLE_STATUSES.has(response.status)
    ) {
      await wait(READ_RETRY_DELAYS_MS[attempt]);
      continue;
    }
    break;
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error?.code ??
        (response.status >= 500
          ? "errors.serviceUnavailable"
          : "errors.unexpected"),
      payload?.error?.details,
    );
  }

  return payload;
}

/**
 * Petición que va entregando lo que llega, en vez de esperar al final.
 *
 * El servidor habla SSE (`text/event-stream`), pero aquí se lee con `fetch` y
 * no con `EventSource`: `EventSource` solo sabe hacer GET, no admite cuerpo y
 * no se puede cancelar con un `AbortController`, que es justo lo que hace
 * falta para el botón de detener.
 *
 * `onEvento` recibe cada objeto ya interpretado. Si la conexión se corta antes
 * de tiempo, el error sube tal cual: quien llama decide si reintenta por la
 * vía clásica o lo enseña.
 */
async function stream(path, body, { signal, onEvento } = {}) {
  let respuesta;
  try {
    respuesta = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new ApiError(0, "errors.network");
  }

  if (!respuesta.ok) {
    const carga = await respuesta.json().catch(() => null);
    throw new ApiError(
      respuesta.status,
      carga?.error?.code ?? "errors.unexpected",
      carga?.error?.details,
    );
  }
  if (!respuesta.body) throw new ApiError(502, "errors.unexpected");

  const lector = respuesta.body.getReader();
  const decodificador = new TextDecoder();
  let pendiente = "";

  for (;;) {
    const { done, value } = await lector.read();
    if (done) break;

    pendiente += decodificador.decode(value, { stream: true });

    // Los eventos van separados por una línea en blanco. El último trozo
    // puede venir cortado a la mitad, así que se guarda para la vuelta
    // siguiente en lugar de intentar interpretarlo.
    const bloques = pendiente.split("\n\n");
    pendiente = bloques.pop() ?? "";

    for (const bloque of bloques) {
      const linea = bloque.split("\n").find((fila) => fila.startsWith("data:"));
      if (!linea) continue;
      try {
        onEvento?.(JSON.parse(linea.slice(5).trim()));
      } catch {
        // Un bloque que no es JSON no debe cortar la lectura del resto.
      }
    }
  }
}

export const api = {
  get: (path, options) => request(path, { ...options, method: "GET" }),
  post: (path, body, options) =>
    request(path, { ...options, method: "POST", body }),
  patch: (path, body, options) =>
    request(path, { ...options, method: "PATCH", body }),
  put: (path, body, options) =>
    request(path, { ...options, method: "PUT", body }),
  delete: (path, options) => request(path, { ...options, method: "DELETE" }),
  /** Lectura en directo: entrega cada evento según llega, sin esperar al final. */
  stream,
  upload: (path, formData, options) =>
    request(path, {
      ...options,
      method: "POST",
      body: formData,
      isFormData: true,
    }),
};

/** URL absoluta de un recurso servido por la API (descargas, vistas previas). */
export function apiUrl(path) {
  return `${BASE_URL}${path}`;
}
